import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';

import {
  BasePlanComponentDataType,
  ExperimentalInputEvaluatedValues,
  ExperimentalInputTypedValuesByComponentName,
  ExperimentalInputs,
  ExperimentalInputsByComponentName,
  ExperimentalInputsComponentName,
  FullExperimentPlan,
  PlannedMethodInstanceValues,
} from './ProcedurePlanTypes';

export interface BuildExperimentPlanArgs {
  replicatesPerCondition: Array<number>;
  namesPerCondition: Array<string>;
  descriptionsPerCondition: Array<string | undefined>;
  numConditions: number;
  experimentalInputs: ExperimentalInputs;
  evaluatedValues: ExperimentalInputEvaluatedValues;
}

/**
 * Derives the confirmation-method map directly from experimentalInputs, without
 * requiring a full experiment plan to be built. Use this when only
 * confirmationMethodByParamVersionId is needed (e.g. on a procedure change).
 */
export function deriveConfirmationMethodByParamVersionId(
  experimentalInputs: ExperimentalInputs
): Record<string, ProcedureParameterConfirmationMethod> {
  const confirmationMethodByParamVersionId: Record<string, ProcedureParameterConfirmationMethod> = {};
  for (const {
    ids: paramIdsByComponent,
    parameterConfirmationMethods: parameterConfirmationMethodsByComponent,
  } of Object.values(experimentalInputs)) {
    for (const componentName of Object.values(ExperimentalInputsComponentName)) {
      const paramIds = paramIdsByComponent[componentName];
      const paramConfirmMethods = parameterConfirmationMethodsByComponent[componentName];
      assert(
        paramIds.length === paramConfirmMethods.length,
        'Parameter id list must be alignable with parameter confirmation method list'
      );
      paramIds.forEach((paramId, paramIndex) => {
        confirmationMethodByParamVersionId[paramId] = paramConfirmMethods[paramIndex];
      });
    }
  }
  return confirmationMethodByParamVersionId;
}

class ExperimentPlanBuilder {
  private buildParams: BuildExperimentPlanArgs;

  constructor(buildParams: BuildExperimentPlanArgs) {
    this.buildParams = buildParams;
  }

  buildExperimentPlan(): FullExperimentPlan {
    const experimentPlanPerCondition: Array<Array<PlannedMethodInstanceValues>> = [];
    for (let conditionNum = 0; conditionNum < this.buildParams.numConditions; conditionNum++) {
      const methodInstanceValues = this.buildAllPlannedMethodInstancesForCondition(conditionNum);
      experimentPlanPerCondition.push(methodInstanceValues);
    }
    return {
      replicatesPerCondition: this.buildParams.replicatesPerCondition,
      namesPerCondition: this.buildParams.namesPerCondition,
      descriptionsPerCondition: this.buildParams.descriptionsPerCondition,
      experimentPlanPerCondition,
      confirmationMethodByParamVersionId: this.buildConfirmationMethodByParamVersionId(),
    };
  }

  private buildAllPlannedMethodInstancesForCondition(
    conditionNum: number
  ): Array<PlannedMethodInstanceValues> {
    const plannedMethodInstances: Array<PlannedMethodInstanceValues> = [];
    for (const [methodId, experimentalInputsForMethod] of Object.entries(
      this.buildParams.experimentalInputs
    )) {
      plannedMethodInstances.push({
        ...this.buildPlannedMethodInstanceForCondition(
          this.buildParams.evaluatedValues[methodId].values[conditionNum],
          experimentalInputsForMethod.parameterNames,
          experimentalInputsForMethod.ids
        ),
        methodId,
      });
    }

    return plannedMethodInstances;
  }

  private buildPlannedMethodInstanceForCondition(
    valuesForCondition: ExperimentalInputTypedValuesByComponentName,
    parameterNames: ExperimentalInputsByComponentName,
    fieldIds: ExperimentalInputsByComponentName
  ): Pick<PlannedMethodInstanceValues, 'parameterValues' | 'materialInputs' | 'equipmentInputs'> {
    const instanceValueByComponentType: Record<
      ExperimentalInputsComponentName,
      Array<BasePlanComponentDataType>
    > = {
      [ExperimentalInputsComponentName.PARAMETERS]: [],
      [ExperimentalInputsComponentName.EQUIPMENT]: [],
      [ExperimentalInputsComponentName.MATERIAL_INPUTS]: [],
    };

    for (const componentName of Object.values(ExperimentalInputsComponentName)) {
      const valuesForConditionAndComponent = valuesForCondition[componentName];
      const parameterNamesForComponent = parameterNames[componentName];
      const fieldIdsForComponent = fieldIds[componentName];
      const numParameterNames = parameterNamesForComponent.length;

      for (let parameterIndex = 0; parameterIndex < numParameterNames; parameterIndex++) {
        const sourceId = fieldIdsForComponent[parameterIndex];
        instanceValueByComponentType[componentName].push({
          name: parameterNamesForComponent[parameterIndex],
          sourceId,
          values: valuesForConditionAndComponent[parameterIndex],
        });
      }
    }
    return {
      parameterValues: instanceValueByComponentType[ExperimentalInputsComponentName.PARAMETERS],
      materialInputs: instanceValueByComponentType[ExperimentalInputsComponentName.MATERIAL_INPUTS],
      equipmentInputs: instanceValueByComponentType[ExperimentalInputsComponentName.EQUIPMENT],
    };
  }

  private buildConfirmationMethodByParamVersionId(): Record<string, ProcedureParameterConfirmationMethod> {
    return deriveConfirmationMethodByParamVersionId(this.buildParams.experimentalInputs);
  }
}

export default function buildFullExperimentPlan(
  buildExperimentParams: BuildExperimentPlanArgs
): FullExperimentPlan {
  const experimentPlanBuilder = new ExperimentPlanBuilder(buildExperimentParams);

  return experimentPlanBuilder.buildExperimentPlan();
}
